import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';
import type { ExternalChatConfig, PromptVersion } from './types';

interface ListVersionsResponse {
  data?: { versions: PromptVersion[] };
  detail?: string;
}

interface CreateVersionResponse {
  data?: PromptVersion;
  detail?: string;
}

interface ActivateVersionResponse {
  /** `config` is typed optional because the endpoint has no schema guarantee
   *  the caller can rely on — a 200 whose body omits it must not blow up a
   *  caller that has already been told the activation succeeded. */
  data?: { status: string; config?: Partial<ExternalChatConfig> };
  detail?: string;
}

interface DeleteVersionResponse {
  data?: { status: string };
  detail?: string;
}

export const listInstructionVersions = async (
  collectionId: string,
): Promise<ListVersionsResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<{ versions: PromptVersion[] }>({
    url: `/${orgId}/collections/${collectionId}/external-chat/instructions/versions`,
    method: 'GET',
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to list instruction versions',
    };
  }

  return response as ListVersionsResponse;
};

export const createInstructionVersion = async (
  collectionId: string,
  params: { prompt: string; name?: string },
): Promise<CreateVersionResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<PromptVersion>({
    url: `/${orgId}/collections/${collectionId}/external-chat/instructions/versions`,
    method: 'POST',
    body: params as Record<string, unknown>,
    target: 'platform',
    // 400 here means the 20-version cap was hit — an expected, user-facing
    // condition (surfaced via notification.error), not a bug to report.
    suppressSentryStatuses: [400],
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to save instruction version',
    };
  }

  return response as CreateVersionResponse;
};

export const activateInstructionVersion = async (
  collectionId: string,
  promptId: string,
): Promise<ActivateVersionResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<{
    status: string;
    config?: Partial<ExternalChatConfig>;
  }>({
    url: `/${orgId}/collections/${collectionId}/external-chat/instructions/versions/${promptId}/activate`,
    method: 'POST',
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to activate instruction version',
    };
  }

  return response as ActivateVersionResponse;
};

export const deleteInstructionVersion = async (
  collectionId: string,
  promptId: string,
): Promise<DeleteVersionResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<{ status: string }>({
    url: `/${orgId}/collections/${collectionId}/external-chat/instructions/versions/${promptId}`,
    method: 'DELETE',
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to delete instruction version',
    };
  }

  return response as DeleteVersionResponse;
};
